<script lang="ts">
	import { format } from 'svelte-i18n';
	import { ButtonV2, Container, Input, Modal, Text } from '@Shelly/shelly-design-system';
	import ImageList from 'src/components/Lists/ImageListV2.svelte';
	import { home_data } from 'src/stores';
	import { ENERGY_DASHBOARD_ID } from './v2/energy-v2-types';
	import {
		getEnergyDashboardVisualSettings,
		saveEnergyDashboardVisualSettings,
	} from './helpers/energy-dashboard-settings';
	import { energyV2Actions } from './v2/energy-v2-ui';
	import { createAsyncAction } from 'src/helpers/async_action_guard';
	import { getBackgroundColorPreviewImages } from 'src/helpers/background_color_helpers';

	const DEFAULT_DASH_ICON = 'fat fa-objects-column';

	export let showModal = false;

	let iconDraft = DEFAULT_DASH_ICON;
	let backgroundDraft = 'color-0';
	let nameLabel = '';
	let modalWasOpen = false;
	const backgroundColorPreviewImages = getBackgroundColorPreviewImages();

	const { loading: isSaving, run: runSave } = createAsyncAction();

	const ICONS_DB = [
		{ id: 0, icon: DEFAULT_DASH_ICON, type: 'icon', isIcon: true },
		{ id: 1, icon: 'fat fa-microchip', type: 'icon', isIcon: true },
		{ id: 2, icon: 'fat fa-bolt', type: 'icon', isIcon: true },
		{ id: 3, icon: 'fat fa-garage', type: 'icon', isIcon: true },
		{ id: 4, icon: 'fat fa-server', type: 'icon', isIcon: true },
	];

	$: icons = ICONS_DB.map((item) => ({
		...item,
		selected: item.icon === iconDraft,
	}));

	const loadDraft = () => {
		const dashboardId = Number($energyV2Actions?.dashboardId ?? ENERGY_DASHBOARD_ID);
		const visual = getEnergyDashboardVisualSettings(dashboardId, $home_data);
		iconDraft = visual.icon?.trim() || DEFAULT_DASH_ICON;
		backgroundDraft = visual.backgroundColor?.trim() || 'color-0';
		nameLabel = visual.name;
	};

	$: if (showModal && !modalWasOpen) {
		modalWasOpen = true;
		loadDraft();
	}

	$: if (!showModal && modalWasOpen) {
		modalWasOpen = false;
	}

	const onClose = () => {
		showModal = false;
	};

	const onSelectImage = (event: CustomEvent) => {
		iconDraft = event.detail.icon || iconDraft || DEFAULT_DASH_ICON;
		backgroundDraft = event.detail.backgroundColor || backgroundDraft || 'color-0';
	};

	const onSave = () =>
		runSave(async () => {
			const dashboardId = Number($energyV2Actions?.dashboardId ?? ENERGY_DASHBOARD_ID);
			const visual = {
				icon: iconDraft.trim() || DEFAULT_DASH_ICON,
				backgroundColor: backgroundDraft.trim() || 'color-0',
			};

			if (dashboardId === ENERGY_DASHBOARD_ID) {
				await saveEnergyDashboardVisualSettings(dashboardId, visual);
			} else {
				await $energyV2Actions?.onVisualSave?.(visual);
			}
			onClose();
		});
</script>

<Modal
	{showModal}
	on:close={onClose}
	title={$format('label_dashboard_settings', { default: 'Dashboard Settings' })}
>
	<Container slot="content" padding="0">
		<Container noScroll padding="0" class="mb-m">
			<Text class="mb-s text-capitalize text-500" element="h2">
				{$format('label_menu_items_dashboard', { default: 'dashboard' })}&nbsp;{$format('name', {
					default: 'name',
				})}
			</Text>

			<Input
				aria-label={`${$format('label_menu_items_dashboard', { default: 'dashboard' })} ${$format(
					'name',
					{ default: 'name' },
				)}`}
				label={`${$format('label_menu_items_dashboard', { default: 'dashboard' })} ${$format(
					'name',
					{
						default: 'name',
					},
				)}`}
				icon="fas fa-objects-column"
				value={nameLabel}
				disabled
				id="energy-dashboard-tab-name-input"
			/>
		</Container>

		<Container noScroll padding="0">
			<Text element="h2" class="mb-m text-500">
				{$format('select_image', { default: 'Select image' })}
			</Text>

			<ImageList
				selectedBackgroundColor={backgroundDraft}
				selectedIcon={iconDraft}
				iconsDB={icons}
				requestType="dashboard"
				showUpload={false}
				showTypeImages={false}
				showOtherImages={false}
				showIcons
				showColors
				coexistIconAndBackgroundColor
				restrictColorsToPreviewImages
				{backgroundColorPreviewImages}
				on:select-image={onSelectImage}
			/>
		</Container>
	</Container>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			on:click={onClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			loading={$isSaving}
			aria-label={$format('button_save', { default: 'Save' })}
			on:click={onSave}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>
